import { InstrumentT, CurrencyT, PriceT, InstrumentPriceBarT, OrderT, ConfigT } from 'types';
import { GqlClient, GqlConnection } from '../gql';
import { HeadersClient, RequestCallbacks } from '../gql/connection';
import tradeQql from './trade-qql';
import { GetInstrumentPriceBarsPayload } from './dto';
export interface ClientWithInstrument {
  instrument: string | null;
  client: GqlConnection;
}

export interface ClientWithAddress {
  address: string | null;
  client: GqlConnection;
}

export default class MarketService {
  public gqlRequestClient: GqlClient;
  public orderBookWsClient: ClientWithInstrument;
  public instrumentPriceWsClient: ClientWithInstrument;
  public openOrdersWsClient: ClientWithAddress;
  public userEventsWsClient: GqlConnection;
  public priceBars: { [instrument: string]: GqlConnection };

  constructor(gqlRequestClient: GqlClient) {
    this.priceBars = {};
    this.gqlRequestClient = gqlRequestClient;
    this.userEventsWsClient = new GqlConnection(this._getGqlHeaders());
    this.orderBookWsClient = {
      client: new GqlConnection(this._getGqlHeaders()),
      instrument: null,
    };
    this.instrumentPriceWsClient = {
      client: new GqlConnection(this._getGqlHeaders()),
      instrument: null,
    };
    this.openOrdersWsClient = {
      client: new GqlConnection(this._getGqlHeaders()),
      address: null,
    };
  }

  private _getGqlHeaders(headers?: HeadersClient, authorization?: string) {
    return {
      ...(headers || {}),
      authorization: authorization || this.gqlRequestClient.authorization,
    };
  }

  public getInstruments(): Promise<{
    instruments: Array<InstrumentT>;
  }> {
    return this.gqlRequestClient.request(tradeQql.getInstruments());
  }

  public getConfig(): Promise<{
    config: ConfigT;
  }> {
    return this.gqlRequestClient.request(tradeQql.getConfig());
  }

  public getInstrumentPriceBars(params: GetInstrumentPriceBarsPayload): Promise<{
    instrument_price_bars: Array<InstrumentPriceBarT>;
  }> {
    return this.gqlRequestClient.request(tradeQql.getInstrumentPriceBars(params));
  }

  public getCurrencies(): Promise<{
    currencies: Array<CurrencyT>;
  }> {
    return this.gqlRequestClient.request(tradeQql.getCurrencies());
  }

  public getPrices(): Promise<{
    instruments: Array<PriceT>;
  }> {
    return this.gqlRequestClient.request(tradeQql.getPrices());
  }

  // ORDERS
  public subscribeOpenOrders(address: string, callbacks: RequestCallbacks) {
    if (address !== this.openOrdersWsClient.address) {
      this.unsubscribeOpenOrders();
      this.openOrdersWsClient.address = address;
      this.openOrdersWsClient.client.subscribe(tradeQql.subscribeOpenOrders(address), callbacks);
    }
  }

  public unsubscribeOpenOrders() {
    this.openOrdersWsClient.address = null;
    this.openOrdersWsClient.client?.disconnect();
  }

  public getOpenOrders(address: string): Promise<{
    open_orders: Array<OrderT>;
  }> {
    return this.gqlRequestClient.request(tradeQql.getOpenOrders(address));
  }

  public getClosedOrders(
    address: `0x${string}`,
    offset?: number,
    limit?: number
  ): Promise<{
    closed_orders: Array<OrderT>;
    closed_orders_count: number;
  }> {
    return this.gqlRequestClient.request(tradeQql.getClosedOrders(address, offset, limit));
  }

  public createOrder(address: string) {
    return this.gqlRequestClient.request(tradeQql.createOrder(address));
  }

  public cancelOrder(
    id: string,
    address: `0x${string}`
  ): Promise<{
    cancel_order: { instrument_id: string };
  }> {
    return this.gqlRequestClient.request(tradeQql.cancelOrder(id, address));
  }

  public downloadClosedOrdersCsv(address: string): Promise<{
    closed_orders_csv: string;
  }> {
    return this.gqlRequestClient.request(tradeQql.downloadClosedOrdersCsv(address));
  }

  public downloadOpenOrdersCsv(address: string): Promise<{
    open_orders_csv: string;
  }> {
    return this.gqlRequestClient.request(tradeQql.downloadOpenOrdersCsv(address));
  }

  // We can't have more than 1 orderbook in time (no reason to have additional subscription posibility)
  public subscribeOrderbook(instrument_id: string, callbacks: RequestCallbacks) {
    if (instrument_id !== this.orderBookWsClient.instrument) {
      this.unsubscribeOrderbook();
      this.orderBookWsClient.instrument = instrument_id;
      this.orderBookWsClient.client.subscribe(tradeQql.subscribeOrderbook(instrument_id), callbacks);
    }
  }

  public unsubscribeOrderbook() {
    this.orderBookWsClient.instrument = null;
    this.orderBookWsClient.client?.disconnect();
  }

  public subscribeInstrumentPrice(instrument_id: string, callbacks: RequestCallbacks) {
    if (instrument_id !== this.instrumentPriceWsClient.instrument) {
      this.unsubscribeInstrumentPrice();
    }

    this.instrumentPriceWsClient.instrument = instrument_id;
    this.instrumentPriceWsClient.client.subscribe(tradeQql.subscribeInstrumentPrice(instrument_id), callbacks);
  }

  public unsubscribeInstrumentPrice() {
    this.instrumentPriceWsClient.instrument = null;
    this.instrumentPriceWsClient.client?.disconnect();
  }
}
